// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import GatewayConnection from '@app/features/gateway/transport/GatewayConnection';
import Guilds from '@app/features/guild/state/Guilds';
import {GuildMember} from '@app/features/member/models/GuildMember';
import {
	buildMemberListLayout,
	getGroupLayoutForRow,
	getTotalMemberCount,
	getTotalRowsFromLayout,
} from '@app/features/member/utils/MemberListLayout';
import {
	isIndexInMemberListRanges,
	type MemberListRanges,
	type NormalizedMemberListRanges,
	normalizeMemberListRanges,
} from '@app/features/member/utils/MemberListRangeUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import type {CustomStatus, GatewayCustomStatusPayload} from '@app/features/user/state/CustomStatus';
import {fromGatewayCustomStatus} from '@app/features/user/state/CustomStatus';
import {CustomStatusEmitter} from '@app/features/user/state/CustomStatusEmitter';
import Window from '@app/features/window/state/Window';
import {GuildOperations} from '@fluxer/constants/src/GuildConstants';
import type {StatusType} from '@fluxer/constants/src/StatusConstants';
import {StatusTypes} from '@fluxer/constants/src/StatusConstants';
import type {GuildMemberData} from '@fluxer/schema/src/domains/guild/GuildMemberSchemas';
import {makeAutoObservable, observable} from 'mobx';

interface MemberListGroup {
	id: string;
	count: number;
}

interface MemberListMember {
	userId: string;
	member: MemberListOperationMember;
}

interface MemberListItem {
	type: 'member';
	data: MemberListMember;
}

interface MemberListState {
	hasReceivedInitialPayload: boolean;
	memberCount: number;
	onlineCount: number;
	groups: Array<MemberListGroup>;
	rows: Map<number, MemberListRow>;
	items: Map<number, MemberListItem>;
	subscribedRanges: NormalizedMemberListRanges;
	presences: Map<string, StatusType>;
	customStatuses: Map<string, CustomStatus | null>;
	knownCustomStatuses: Map<string, CustomStatus | null>;
}

interface MemberListRow {
	type: 'group' | 'member';
	group?: MemberListGroup;
	userId?: string;
	member?: MemberListOperationMember;
	presence?: {
		status?: string;
		custom_status?: GatewayCustomStatusPayload | null;
	} | null;
}

type MemberListOperationMember = GuildMemberData & {
	presence?: {
		status?: string;
		custom_status?: GatewayCustomStatusPayload | null;
	} | null;
};

interface MemberListOperation {
	op: 'SYNC';
	range: [number, number];
	items: ReadonlyArray<{
		member?: MemberListOperationMember;
		group?: MemberListGroup;
	}>;
}

interface MemberListUpdateParams {
	guildId: string;
	listId: string;
	channelId?: string;
	memberCount: number;
	onlineCount: number;
	groups: ReadonlyArray<MemberListGroup>;
	ops: ReadonlyArray<MemberListOperation>;
}

interface PendingMemberListUpdateBatch {
	guildId: string;
	listId: string;
	timeoutId: number;
	updates: Array<MemberListUpdateParams>;
}

const MEMBER_LIST_TTL_MS = 5 * 60 * 1000;
const MEMBER_LIST_PRUNE_INTERVAL_MS = 30 * 1000;
const MEMBER_LIST_UPDATE_BATCH_MS = 1000;
const EMPTY_MEMBER_LIST_RANGES = normalizeMemberListRanges([]);

function areCustomStatusesEqual(
	left: CustomStatus | null | undefined,
	right: CustomStatus | null | undefined,
): boolean {
	if (left == null || right == null) {
		return left == null && right == null;
	}
	return (
		left.text === right.text &&
		left.expiresAt === right.expiresAt &&
		left.emojiId === right.emojiId &&
		left.emojiName === right.emojiName &&
		(left.emojiAnimated ?? null) === (right.emojiAnimated ?? null)
	);
}

function areRangesEqual(left?: Array<[number, number]>, right?: Array<[number, number]>): boolean {
	const leftRanges = left ?? [];
	const rightRanges = right ?? [];
	if (leftRanges.length !== rightRanges.length) {
		return false;
	}
	for (let i = 0; i < leftRanges.length; i++) {
		const [leftStart, leftEnd] = leftRanges[i];
		const [rightStart, rightEnd] = rightRanges[i];
		if (leftStart !== rightStart || leftEnd !== rightEnd) {
			return false;
		}
	}
	return true;
}

class MemberSidebar {
	private logger = new Logger('MemberSidebar');
	lists: Record<string, Record<string, MemberListState>> = {};
	channelListIds: Record<string, Record<string, string>> = {};
	lastAccess: Record<string, Record<string, number>> = {};
	pruneIntervalId: number | null = null;
	sessionVersion = 0;
	private materializedMemberCache = new WeakMap<MemberListMember, GuildMember>();
	private pendingListUpdateBatches = new Map<string, PendingMemberListUpdateBatch>();

	constructor() {
		makeAutoObservable<this, 'lastAccess' | 'pruneIntervalId' | 'materializedMemberCache' | 'pendingListUpdateBatches'>(
			this,
			{
				lists: observable.ref,
				lastAccess: false,
				pruneIntervalId: false,
				materializedMemberCache: false,
				pendingListUpdateBatches: false,
			},
			{autoBind: true},
		);
		this.startPruneInterval();
	}

	handleSessionInvalidated(): void {
		this.clearPendingListUpdateBatches();
		this.lists = {};
		this.channelListIds = {};
		this.lastAccess = {};
		this.sessionVersion += 1;
	}

	handleGuildDelete(guildId: string): void {
		this.clearPendingListUpdateBatches(guildId);
		if (this.lists[guildId]) {
			const {[guildId]: _, ...remainingLists} = this.lists;
			this.lists = remainingLists;
		}
		if (this.channelListIds[guildId]) {
			const {[guildId]: _, ...remainingMappings} = this.channelListIds;
			this.channelListIds = remainingMappings;
		}
		if (this.lastAccess[guildId]) {
			const {[guildId]: _, ...remainingAccess} = this.lastAccess;
			this.lastAccess = remainingAccess;
		}
	}

	handleGuildCreate(guildId: string): void {
		this.clearPendingListUpdateBatches(guildId);
		if (this.lists[guildId]) {
			const {[guildId]: _, ...remainingLists} = this.lists;
			this.lists = remainingLists;
		}
		if (this.channelListIds[guildId]) {
			const {[guildId]: _, ...remainingMappings} = this.channelListIds;
			this.channelListIds = remainingMappings;
		}
		if (this.lastAccess[guildId]) {
			const {[guildId]: _, ...remainingAccess} = this.lastAccess;
			this.lastAccess = remainingAccess;
		}
	}

	handleListUpdate(params: MemberListUpdateParams): void {
		const {guildId, listId, channelId} = params;
		if (this.isMemberListUpdatesDisabled(guildId)) {
			return;
		}
		const existingGuildLists = this.lists[guildId] ?? {};
		const existingList = existingGuildLists[listId] ?? (channelId ? existingGuildLists[channelId] : undefined);
		if (!existingList?.hasReceivedInitialPayload || typeof window === 'undefined') {
			this.applyListUpdate(params);
			return;
		}
		if (this.shouldBypassListUpdateBatchForCurrentUserPresence(params, existingList)) {
			this.flushPendingListUpdateBatch(this.getListUpdateBatchKey(guildId, listId));
			this.applyListUpdate(params);
			return;
		}
		this.queueListUpdate(params);
	}

	private queueListUpdate(params: MemberListUpdateParams): void {
		const {guildId, listId} = params;
		const batchKey = this.getListUpdateBatchKey(guildId, listId);
		let batch = this.pendingListUpdateBatches.get(batchKey);
		if (!batch) {
			batch = {
				guildId,
				listId,
				timeoutId: window.setTimeout(() => this.flushPendingListUpdateBatch(batchKey), MEMBER_LIST_UPDATE_BATCH_MS),
				updates: [],
			};
			this.pendingListUpdateBatches.set(batchKey, batch);
		}
		batch.updates.push(this.cloneListUpdateParams(params));
		this.touchList(guildId, listId);
	}

	flushPendingListUpdates(): void {
		for (const batchKey of Array.from(this.pendingListUpdateBatches.keys())) {
			this.flushPendingListUpdateBatch(batchKey);
		}
	}

	private flushPendingListUpdateBatch(batchKey: string): void {
		const batch = this.pendingListUpdateBatches.get(batchKey);
		if (!batch) {
			return;
		}
		this.pendingListUpdateBatches.delete(batchKey);
		window.clearTimeout(batch.timeoutId);
		for (const update of batch.updates) {
			this.applyListUpdate(update);
		}
	}

	private clearPendingListUpdateBatches(guildId?: string): void {
		for (const [batchKey, batch] of Array.from(this.pendingListUpdateBatches.entries())) {
			if (guildId !== undefined && batch.guildId !== guildId) {
				continue;
			}
			window.clearTimeout(batch.timeoutId);
			this.pendingListUpdateBatches.delete(batchKey);
		}
	}

	private clearPendingListUpdateBatch(guildId: string, listId: string): void {
		const batchKey = this.getListUpdateBatchKey(guildId, listId);
		const batch = this.pendingListUpdateBatches.get(batchKey);
		if (!batch) {
			return;
		}
		window.clearTimeout(batch.timeoutId);
		this.pendingListUpdateBatches.delete(batchKey);
	}

	private getListUpdateBatchKey(guildId: string, listId: string): string {
		return `${guildId}\u0000${listId}`;
	}

	private cloneListUpdateParams(params: MemberListUpdateParams): MemberListUpdateParams {
		return {
			...params,
			groups: params.groups.map((group) => ({...group})),
			ops: params.ops.map((op) => ({
				op: op.op,
				range: [...op.range] as [number, number],
				items: op.items.map((item) => ({
					member: item.member,
					group: item.group ? {...item.group} : undefined,
				})),
			})),
		};
	}

	private shouldBypassListUpdateBatchForCurrentUserPresence(
		params: MemberListUpdateParams,
		listState: MemberListState,
	): boolean {
		const currentUserId = Authentication.currentUserId;
		if (!currentUserId || listState.subscribedRanges.length === 0) {
			return false;
		}
		let currentUserVisible = false;
		for (const item of listState.items.values()) {
			if (item.data.userId === currentUserId) {
				currentUserVisible = true;
				break;
			}
		}
		if (!currentUserVisible) {
			return false;
		}
		for (const op of params.ops) {
			for (const item of op.items) {
				const member = item.member;
				if (member?.user?.id !== currentUserId) {
					continue;
				}
				if (this.memberPresencePayloadChanged(listState, currentUserId, member.presence ?? null)) {
					return true;
				}
			}
		}
		return false;
	}

	private memberPresencePayloadChanged(
		listState: MemberListState,
		userId: string,
		presence: MemberListOperationMember['presence'],
	): boolean {
		if (!presence) {
			return false;
		}
		if (presence.status != null) {
			const nextStatus = this.normalizeStatus(presence.status);
			const currentStatus = listState.presences.get(userId) ?? StatusTypes.OFFLINE;
			if (nextStatus !== currentStatus) {
				return true;
			}
		}
		if (Object.hasOwn(presence, 'custom_status')) {
			const nextCustomStatus = fromGatewayCustomStatus(presence.custom_status ?? null);
			const currentCustomStatus = listState.customStatuses.has(userId)
				? (listState.customStatuses.get(userId) ?? null)
				: (listState.knownCustomStatuses.get(userId) ?? null);
			if (!areCustomStatusesEqual(currentCustomStatus, nextCustomStatus)) {
				return true;
			}
		}
		return false;
	}

	private applyListUpdate(params: MemberListUpdateParams): void {
		const {guildId, listId, channelId, memberCount, onlineCount, groups, ops} = params;
		if (this.isMemberListUpdatesDisabled(guildId)) {
			return;
		}
		const storageKey = listId;
		const existingGuildLists = this.lists[guildId] ?? {};
		const guildLists: Record<string, MemberListState> = {...existingGuildLists};
		if (channelId) {
			this.registerChannelListId(guildId, channelId, listId);
			if (guildLists[channelId] && !guildLists[storageKey]) {
				guildLists[storageKey] = guildLists[channelId];
				delete guildLists[channelId];
			}
		}
		if (!guildLists[storageKey]) {
			guildLists[storageKey] = this.createEmptyListState();
		}
		const listState = guildLists[storageKey];
		const newRows = new Map(listState.rows);
		const changedCustomStatusUserIds = new Set<string>();
		this.touchList(guildId, storageKey);
		for (const op of ops) {
			const [start, end] = op.range;
			for (let i = start; i <= end; i++) {
				newRows.delete(i);
			}
			let nextIndex = start;
			for (const rawItem of op.items) {
				const row = this.convertRow(rawItem);
				if (row) {
					newRows.set(nextIndex, row);
					nextIndex += 1;
				} else if (!this.isEmptyGroupItem(rawItem)) {
					nextIndex += 1;
				}
			}
		}
		const visibleGroups = this.visibleGroups(groups);
		const groupLayouts = buildMemberListLayout(visibleGroups);
		const totalMembers = Math.max(memberCount, getTotalMemberCount(visibleGroups));
		const totalRows = groupLayouts.length > 0 ? getTotalRowsFromLayout(groupLayouts) : totalMembers;
		const boundedRows = new Map<number, MemberListRow>();
		for (const [index, row] of newRows) {
			if (index < 0 || index >= totalRows) {
				continue;
			}
			boundedRows.set(index, row);
		}
		const subscribedRanges = normalizeMemberListRanges(listState.subscribedRanges);
		const prunedRows =
			subscribedRanges.length > 0 ? this.pruneRowsToRanges(boundedRows, subscribedRanges) : boundedRows;
		const newItems = new Map<number, MemberListItem>();
		const newPresences = new Map<string, StatusType>();
		const newCustomStatuses = new Map<string, CustomStatus | null>();
		const nextKnownCustomStatuses = new Map(listState.knownCustomStatuses);
		const userIdRowCounts = new Map<string, number>();
		const recordCustomStatus = (userId: string, customStatus: CustomStatus | null) => {
			newCustomStatuses.set(userId, customStatus);
			const hadKnownCustomStatus = nextKnownCustomStatuses.has(userId);
			const previousKnownCustomStatus = hadKnownCustomStatus
				? (nextKnownCustomStatuses.get(userId) ?? null)
				: undefined;
			if (!hadKnownCustomStatus || !areCustomStatusesEqual(previousKnownCustomStatus ?? null, customStatus)) {
				changedCustomStatusUserIds.add(userId);
			}
			nextKnownCustomStatuses.set(userId, customStatus);
		};
		const clearKnownCustomStatus = (userId: string) => {
			if ((nextKnownCustomStatuses.get(userId) ?? null) == null) {
				return;
			}
			recordCustomStatus(userId, null);
		};
		this.visitRowsInIndexOrder(prunedRows, subscribedRanges, (rowIndex, row) => {
			if (row.type !== 'member' || !row.userId) {
				return;
			}
			let rowGroupId: string | null = null;
			if (groupLayouts.length > 0) {
				const layout = getGroupLayoutForRow(groupLayouts, rowIndex);
				if (!layout || rowIndex === layout.headerRowIndex) {
					return;
				}
				rowGroupId = layout.id;
			}
			userIdRowCounts.set(row.userId, (userIdRowCounts.get(row.userId) ?? 0) + 1);
			const memberItem = this.convertItem(guildId, row);
			if (memberItem) {
				newItems.set(rowIndex, memberItem);
			}
			const presenceStatus = this.extractPresenceFromRow(row);
			if (presenceStatus) {
				newPresences.set(row.userId, presenceStatus);
			}
			if (row.presence && Object.hasOwn(row.presence, 'custom_status')) {
				const customStatus = fromGatewayCustomStatus(row.presence.custom_status ?? null);
				recordCustomStatus(row.userId, customStatus);
			} else if (presenceStatus === StatusTypes.OFFLINE || (row.presence == null && rowGroupId === 'offline')) {
				clearKnownCustomStatus(row.userId);
			}
		});
		const duplicateUserIds: Array<string> = [];
		for (const [userId, count] of userIdRowCounts) {
			if (count > 1) {
				duplicateUserIds.push(userId);
			}
		}
		listState.memberCount = memberCount;
		listState.onlineCount = onlineCount;
		listState.groups = visibleGroups;
		listState.rows = prunedRows;
		listState.items = newItems;
		listState.presences = newPresences;
		listState.customStatuses = newCustomStatuses;
		listState.knownCustomStatuses = nextKnownCustomStatuses;
		listState.subscribedRanges = subscribedRanges;
		listState.hasReceivedInitialPayload = true;
		this.lists = {...this.lists, [guildId]: {...guildLists, [storageKey]: listState}};
		if (duplicateUserIds.length > 0) {
			const uniqueDuplicateUserIds = Array.from(new Set(duplicateUserIds));
			this.logger.warn('Duplicate member rows received in list update:', {
				guildId,
				listId: storageKey,
				duplicateCount: uniqueDuplicateUserIds.length,
				userIds: uniqueDuplicateUserIds.slice(0, 25),
			});
		}
		if (changedCustomStatusUserIds.size > 0) {
			queueMicrotask(() => {
				for (const userId of changedCustomStatusUserIds) {
					CustomStatusEmitter.emitMemberListChange(guildId, storageKey, userId);
				}
			});
		}
	}

	private convertRow(rawItem: {member?: MemberListOperationMember; group?: MemberListGroup}): MemberListRow | null {
		if (rawItem.group) {
			if (Math.max(0, rawItem.group.count) === 0) {
				return null;
			}
			return {
				type: 'group',
				group: rawItem.group,
			};
		}
		if (!rawItem.member?.user?.id) {
			return null;
		}
		return {
			type: 'member',
			userId: rawItem.member.user.id,
			member: rawItem.member,
			presence: rawItem.member.presence ?? null,
		};
	}

	private visibleGroups(groups: ReadonlyArray<MemberListGroup>): Array<MemberListGroup> {
		return groups.filter((group) => Math.max(0, group.count) > 0);
	}

	private isEmptyGroupItem(rawItem: {group?: MemberListGroup}): boolean {
		return rawItem.group !== undefined && Math.max(0, rawItem.group.count) === 0;
	}

	private convertItem(guildId: string, row: MemberListRow): MemberListItem | null {
		if (row.member && row.userId) {
			return {
				type: 'member',
				data: {
					userId: row.userId,
					member: row.member,
				},
			};
		}
		this.logger.warn('Member not found in store:', {guildId, userId: row.userId});
		return null;
	}

	materializeItemMember(guildId: string, item: MemberListItem | null | undefined): GuildMember | null {
		if (!item) {
			return null;
		}
		const cachedMember = this.materializedMemberCache.get(item.data);
		if (cachedMember) {
			return cachedMember;
		}
		const member = new GuildMember(guildId, item.data.member, {cacheUser: false});
		this.materializedMemberCache.set(item.data, member);
		return member;
	}

	private extractPresenceFromRow(row: MemberListRow): StatusType | null {
		const status = row.presence?.status;
		if (!status) {
			return null;
		}
		return this.normalizeStatus(status);
	}

	private normalizeStatus(status: string): StatusType {
		switch (status.toLowerCase()) {
			case 'online':
				return StatusTypes.ONLINE;
			case 'idle':
				return StatusTypes.IDLE;
			case 'dnd':
				return StatusTypes.DND;
			default:
				return StatusTypes.OFFLINE;
		}
	}

	subscribeToChannel(
		guildId: string,
		channelId: string,
		ranges: Array<[number, number]>,
		forceSubscriptionUpdate = false,
	): void {
		if (this.isMemberListUpdatesDisabled(guildId)) {
			return;
		}
		const normalizedRanges = normalizeMemberListRanges(ranges);
		const storageKey = this.resolveListKey(guildId, channelId);
		const socket = GatewayConnection.socket;
		const existingGuildLists = this.lists[guildId] ?? {};
		const guildLists: Record<string, MemberListState> = {...existingGuildLists};
		const existingList = guildLists[storageKey];
		const shouldSendUpdate =
			forceSubscriptionUpdate || !areRangesEqual(existingList?.subscribedRanges, normalizedRanges);
		const shouldBootstrapGuildSync = forceSubscriptionUpdate || !existingList;
		if (shouldSendUpdate) {
			socket?.updateGuildSubscriptions({
				subscriptions: {
					[guildId]: {
						active: true,
						...(shouldBootstrapGuildSync ? {sync: true} : {}),
						member_list_channels: {[channelId]: normalizedRanges},
					},
				},
			});
		}
		if (!existingList) {
			guildLists[storageKey] = this.createEmptyListState(normalizedRanges);
		} else {
			guildLists[storageKey] = this.pruneListStateToRanges({
				listState: {...existingList, subscribedRanges: normalizedRanges},
				subscribedRanges: normalizedRanges,
			});
		}
		this.touchList(guildId, storageKey);
		this.lists = {...this.lists, [guildId]: guildLists};
	}

	unsubscribeFromChannel(guildId: string, channelId: string): void {
		const socket = GatewayConnection.socket;
		socket?.updateGuildSubscriptions({
			subscriptions: {
				[guildId]: {
					member_list_channels: {[channelId]: []},
				},
			},
		});
		const storageKey = this.resolveListKey(guildId, channelId);
		this.clearPendingListUpdateBatch(guildId, storageKey);
		const existingGuildLists = this.lists[guildId] ?? {};
		const existingList = existingGuildLists[storageKey];
		if (existingList) {
			const guildLists = {...existingGuildLists};
			guildLists[storageKey] = {
				...existingList,
				subscribedRanges: EMPTY_MEMBER_LIST_RANGES,
			};
			this.lists = {...this.lists, [guildId]: guildLists};
		}
	}

	getSubscribedRanges(guildId: string, channelId: string): NormalizedMemberListRanges {
		const storageKey = this.resolveListKey(guildId, channelId);
		return this.lists[guildId]?.[storageKey]?.subscribedRanges ?? EMPTY_MEMBER_LIST_RANGES;
	}

	getVisibleItems(guildId: string, listId: string, rowRange: [number, number]): Array<MemberListItem> {
		const storageKey = this.resolveListKey(guildId, listId);
		const listState = this.lists[guildId]?.[storageKey];
		if (!listState) {
			return [];
		}
		const [start, end] = rowRange;
		const items: Array<MemberListItem> = [];
		for (let i = start; i <= end; i++) {
			const item = listState.items.get(i);
			if (item) {
				items.push(item);
			}
		}
		return items;
	}

	getList(guildId: string, listId: string): MemberListState | undefined {
		const storageKey = this.resolveListKey(guildId, listId);
		return this.lists[guildId]?.[storageKey];
	}

	getMemberCount(guildId: string, listId: string): number {
		const storageKey = this.resolveListKey(guildId, listId);
		return this.lists[guildId]?.[storageKey]?.memberCount ?? 0;
	}

	getOnlineCount(guildId: string, listId: string): number {
		const storageKey = this.resolveListKey(guildId, listId);
		return this.lists[guildId]?.[storageKey]?.onlineCount ?? 0;
	}

	getPresence(guildId: string, listId: string, userId: string): StatusType | null {
		const storageKey = this.resolveListKey(guildId, listId);
		const listState = this.lists[guildId]?.[storageKey];
		if (!listState) {
			return null;
		}
		return listState.presences.get(userId) ?? null;
	}

	getCustomStatus(guildId: string, listId: string, userId: string): CustomStatus | null | undefined {
		const storageKey = this.resolveListKey(guildId, listId);
		const listState = this.lists[guildId]?.[storageKey];
		if (!listState) {
			return undefined;
		}
		if (listState.customStatuses.has(userId)) {
			return listState.customStatuses.get(userId) ?? null;
		}
		if (listState.knownCustomStatuses.has(userId)) {
			return listState.knownCustomStatuses.get(userId) ?? null;
		}
		return undefined;
	}

	private pruneListStateToRanges(params: {
		listState: MemberListState;
		subscribedRanges: MemberListRanges;
	}): MemberListState {
		const {listState, subscribedRanges} = params;
		const normalizedSubscribedRanges = normalizeMemberListRanges(subscribedRanges);
		if (normalizedSubscribedRanges.length === 0) {
			return {
				...listState,
				subscribedRanges: EMPTY_MEMBER_LIST_RANGES,
				rows: new Map(),
				items: new Map(),
				presences: new Map(),
				customStatuses: new Map(),
			};
		}
		const prunedRows = this.pruneRowsToRanges(listState.rows, normalizedSubscribedRanges);
		const prunedItems = this.pruneItemsToRanges(listState.items, normalizedSubscribedRanges);
		const retainedUserIds = new Set<string>();
		for (const item of prunedItems.values()) {
			retainedUserIds.add(item.data.userId);
		}
		const prunedPresences = new Map<string, StatusType>();
		const prunedCustomStatuses = new Map<string, CustomStatus | null>();
		for (const [userId, status] of listState.presences) {
			if (retainedUserIds.has(userId)) {
				prunedPresences.set(userId, status);
			}
		}
		for (const [userId, customStatus] of listState.customStatuses) {
			if (retainedUserIds.has(userId)) {
				prunedCustomStatuses.set(userId, customStatus);
			}
		}
		return {
			...listState,
			subscribedRanges: normalizedSubscribedRanges,
			rows: prunedRows,
			items: prunedItems,
			presences: prunedPresences,
			customStatuses: prunedCustomStatuses,
		};
	}

	private pruneRowsToRanges(
		rows: Map<number, MemberListRow>,
		subscribedRanges: Array<[number, number]>,
	): Map<number, MemberListRow> {
		if (subscribedRanges.length === 0) {
			return new Map();
		}
		const prunedRows = new Map<number, MemberListRow>();
		for (const [index, row] of rows) {
			if (isIndexInMemberListRanges(index, subscribedRanges)) {
				prunedRows.set(index, row);
			}
		}
		return prunedRows;
	}

	private visitRowsInIndexOrder(
		rows: Map<number, MemberListRow>,
		subscribedRanges: NormalizedMemberListRanges,
		visit: (rowIndex: number, row: MemberListRow) => void,
	): void {
		if (subscribedRanges.length === 0) {
			const sortedRows = Array.from(rows.entries()).sort(([left], [right]) => left - right);
			for (const [rowIndex, row] of sortedRows) {
				visit(rowIndex, row);
			}
			return;
		}
		for (const [start, end] of subscribedRanges) {
			for (let rowIndex = start; rowIndex <= end; rowIndex += 1) {
				const row = rows.get(rowIndex);
				if (row) {
					visit(rowIndex, row);
				}
			}
		}
	}

	private pruneItemsToRanges(
		items: Map<number, MemberListItem>,
		subscribedRanges: Array<[number, number]>,
	): Map<number, MemberListItem> {
		if (subscribedRanges.length === 0) {
			return new Map();
		}
		const prunedItems = new Map<number, MemberListItem>();
		for (const [rowIndex, item] of items) {
			if (isIndexInMemberListRanges(rowIndex, subscribedRanges)) {
				prunedItems.set(rowIndex, item);
			}
		}
		return prunedItems;
	}

	private createEmptyListState(subscribedRanges: MemberListRanges = []): MemberListState {
		return {
			hasReceivedInitialPayload: false,
			memberCount: 0,
			onlineCount: 0,
			groups: [],
			rows: new Map(),
			items: new Map(),
			subscribedRanges: normalizeMemberListRanges(subscribedRanges),
			presences: new Map(),
			customStatuses: new Map(),
			knownCustomStatuses: new Map(),
		};
	}

	private isMemberListUpdatesDisabled(guildId: string): boolean {
		const guild = Guilds.getGuild(guildId);
		if (!guild) {
			return false;
		}
		return (guild.disabledOperations & GuildOperations.MEMBER_LIST_UPDATES) !== 0;
	}

	private touchList(guildId: string, listId: string): void {
		const now = Date.now();
		if (!this.lastAccess[guildId]) {
			this.lastAccess[guildId] = {};
		}
		this.lastAccess[guildId][listId] = now;
	}

	private resolveListKey(guildId: string, listIdOrChannelId: string): string {
		const guildMappings = this.channelListIds[guildId];
		return guildMappings?.[listIdOrChannelId] ?? listIdOrChannelId;
	}

	private registerChannelListId(guildId: string, channelId: string, listId: string): void {
		const guildMappings = this.channelListIds[guildId] ?? {};
		if (guildMappings[channelId] === listId) {
			if (!this.channelListIds[guildId]) {
				this.channelListIds = {...this.channelListIds, [guildId]: guildMappings};
			}
			return;
		}
		this.channelListIds = {
			...this.channelListIds,
			[guildId]: {...guildMappings, [channelId]: listId},
		};
	}

	private startPruneInterval(): void {
		if (this.pruneIntervalId != null) {
			return;
		}
		if (typeof window === 'undefined') {
			return;
		}
		this.pruneIntervalId = window.setInterval(() => this.pruneExpiredLists(), MEMBER_LIST_PRUNE_INTERVAL_MS);
	}

	private pruneExpiredLists(): void {
		if (!Window.focused) {
			return;
		}
		const now = Date.now();
		const ttlCutoff = now - MEMBER_LIST_TTL_MS;
		const updatedLists: Record<string, Record<string, MemberListState>> = {...this.lists};
		const updatedAccess: Record<string, Record<string, number>> = {...this.lastAccess};
		const updatedMappings: Record<string, Record<string, string>> = {...this.channelListIds};
		Object.entries(this.lastAccess).forEach(([guildId, accessMap]) => {
			const guildLists = {...(updatedLists[guildId] ?? {})};
			const guildAccess = {...accessMap};
			const guildMappings = {...(updatedMappings[guildId] ?? {})};
			Object.entries(accessMap).forEach(([listId, lastSeen]) => {
				const listState = guildLists[listId];
				if (listState && listState.subscribedRanges.length > 0) {
					guildAccess[listId] = now;
					return;
				}
				if (lastSeen < ttlCutoff) {
					delete guildLists[listId];
					delete guildAccess[listId];
					Object.entries(guildMappings).forEach(([channelId, mappedListId]) => {
						if (mappedListId === listId) {
							delete guildMappings[channelId];
							const socket = GatewayConnection.socket;
							socket?.updateGuildSubscriptions({
								subscriptions: {
									[guildId]: {
										member_list_channels: {[channelId]: []},
									},
								},
							});
						}
					});
				}
			});
			if (Object.keys(guildLists).length === 0) {
				delete updatedLists[guildId];
			} else {
				updatedLists[guildId] = guildLists;
			}
			if (Object.keys(guildAccess).length === 0) {
				delete updatedAccess[guildId];
			} else {
				updatedAccess[guildId] = guildAccess;
			}
			if (Object.keys(guildMappings).length === 0) {
				delete updatedMappings[guildId];
			} else {
				updatedMappings[guildId] = guildMappings;
			}
		});
		this.lists = updatedLists;
		this.lastAccess = updatedAccess;
		this.channelListIds = updatedMappings;
	}
}

export default new MemberSidebar();
